<!-- Background -->
<div class="fixed inset-0 employee-background -z-10 bg-gray-100"></div>

<!-- Top bar -->
<div class="fixed inset-x-0 top-0 h-14 z-10 flex items-center px-4 sm:px-6 select-none bg-white/80 backdrop-blur-md border-b border-gray-200/60 shadow-sm">
  <img alt="CheqMe" class="h-9 rounded-full border border-gray-200 shadow-sm" src="assets/images/icon.png">
  <span class="ml-3 text-base lg:text-lg text-gray-900 fw-bold">CheqMe</span>
  <button (click)="logout()"
          class="ml-auto inline-flex items-center justify-center px-4 py-2 border border-gray-200 rounded-2xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-semibold text-sm shadow-sm transition-all duration-200 active:scale-95">
    Abmelden
  </button>
</div>

<!-- Content -->
<div class="absolute inset-x-0 top-14 bottom-0 overflow-y-auto">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 py-4 space-y-4">

    <!-- Intro / gate explanation -->
    <div class="p-4 bg-theme-primary-50/50 border border-theme-primary-100/50 rounded-2xl shadow-sm">
      <div class="flex items-start gap-3">
        <div class="p-2 bg-theme-primary-600 text-white rounded-xl shrink-0">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
          </svg>
        </div>
        <div class="min-w-0" data-test="gate-text-column">
          <h1 class="text-xl font-bold text-gray-900 tracking-tight" data-test="gate-heading">Erforderliche Formulare</h1>
          @if (blocking) {
            <p class="mt-1 text-sm text-gray-600 leading-relaxed">
              Bevor Sie fortfahren können, müssen die folgenden Pflichtformulare ausgefüllt bzw. erneut bestätigt
              werden. Sobald alle eingereicht sind, wird Ihr Zugang automatisch freigeschaltet.
            </p>
          } @else {
            <!-- Voluntary mode: these forms do NOT block the session, so say so plainly and name the Frist. -->
            <p class="mt-1 text-sm text-gray-600 leading-relaxed" data-test="pending-intro">
              @if (hasNewForms) {
                Diese Formulare sind Ihnen neu zugewiesen worden. Sie können sie jetzt ausfüllen oder später.
                Ihr Zugang bleibt bis zum Ende der Frist uneingeschränkt.
              } @else {
                Diese Formulare müssen erneut bestätigt werden. Sie können das jetzt erledigen oder später.
                Ihr Zugang bleibt bis zum Ende der Frist uneingeschränkt.
              }@if (pendingDeadline) {<span class="font-semibold"> Frist bis {{ pendingDeadline | date: 'dd.MM.yyyy' }}.</span>}
            </p>
          }
          @if (!loading && !blockedNoAction && total > 0) {
            <p class="mt-2 text-xs font-bold text-theme-primary-700 uppercase tracking-widest">
              Formular {{ currentIndex + 1 }} von {{ total }}
            </p>
          }

          <!-- Inside the TEXT column, deliberately (#503). Both of these used to be siblings of the icon+text
               row, so they began at the icon's left edge and hung out to the left of the heading, the paragraph
               and the "Formular 1 von 1" line they belong to. The icon column is `shrink-0` at every width and
               the row never wraps, so this one position is correct on a phone and on a desktop alike — there is
               no breakpoint at which the text column stops existing. -->
          @if (!blocking && !loading) {
            <button type="button" (click)="postpone()" data-test="postpone"
                    class="mt-3 inline-flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Später erledigen
            </button>
          }

          @if (unavailable) {
            <!-- yellow-800, not -700: this line sits on the card's tinted theme-primary-50/50 surface, where
                 yellow-700 measures 4.49:1 at 12px — a hair under the floor. Caught by the axe check. -->
            <p class="mt-3 text-xs font-semibold text-yellow-800">
              Hinweis: Der Formulardienst ist derzeit eingeschränkt erreichbar.
            </p>
          }
        </div>
      </div>
    </div>

    <!-- Error -->
    @if (errorMessage) {
      <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
        <svg class="h-5 w-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
             xmlns="http://www.w3.org/2000/svg">
          <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
        </svg>
        <div class="min-w-0">
          <p class="text-sm font-semibold text-red-600">{{ errorMessage }}</p>
          <button (click)="refresh()"
                  class="mt-2 inline-flex items-center text-red-600 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">
            Erneut versuchen
          </button>
        </div>
      </div>
    }

    <!-- Some required forms could not be loaded, others could (#502). The ones that loaded are rendered below;
         these are named here rather than dropped, because the gate still counts them — a silently shortened
         wizard would never satisfy it and the employee would have no idea why. All-or-nothing is the red error
         card above instead. -->
    @if (!loading && hasUnavailableForms) {
      <div class="p-4 bg-yellow-50 border border-yellow-100 rounded-2xl shadow-sm flex items-start gap-3"
           data-test="unavailable-forms" role="status">
        <svg class="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
             xmlns="http://www.w3.org/2000/svg">
          <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
        </svg>
        <div class="min-w-0">
          <p class="text-sm font-bold text-yellow-900 tracking-tight">
            @if (unavailableForms.length === 1) {
              Ein Formular konnte nicht geladen werden
            } @else {
              {{ unavailableForms.length }} Formulare konnten nicht geladen werden
            }
          </p>
          <p class="mt-1 text-sm text-yellow-800 leading-relaxed">
            Die übrigen Formulare können Sie jetzt ausfüllen. Diese hier bleiben erforderlich und werden weiter
            angefragt, bis sie wieder geladen werden können:
          </p>
          <ul class="mt-2 space-y-1">
            @for (form of unavailableForms; track form.key) {
              <li class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-yellow-900">
                <span class="font-mono font-semibold break-all">{{ form.key }}</span>
                <span class="text-xs font-semibold text-yellow-800">({{ unavailableKindLabel(form.kind) }})</span>
              </li>
            }
          </ul>
          <p class="mt-2 text-sm text-yellow-800 leading-relaxed">
            Bitte versuchen Sie es später erneut. Bleibt es dabei, wenden Sie sich an Ihre Vorgesetzten.
          </p>
          <button type="button" (click)="refresh()" data-test="unavailable-retry"
                  class="mt-2 inline-flex items-center rounded-md bg-yellow-100 px-3 py-1.5 text-sm font-medium text-yellow-900 transition-all duration-200 hover:bg-yellow-200">
            Erneut versuchen
          </button>
        </div>
      </div>
    }

    <!-- Loading -->
    @if (loading) {
      <div class="flex items-center justify-center py-20">
        <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24"
             xmlns="http://www.w3.org/2000/svg">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
        </svg>
      </div>
    }

    <!-- Blocked, but nothing to fill (overdue review awaiting a reviewer) -->
    @if (!loading && blockedNoAction) {
      <div class="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm text-center">
        <p class="text-base font-bold text-gray-800 tracking-tight">Überprüfung ausstehend</p>
        <p class="mt-2 text-sm text-gray-500 leading-relaxed">
          Eine Ihrer Einreichungen muss von einer berechtigten Person überprüft werden. Sobald das erledigt ist,
          wird Ihr Zugang wieder freigeschaltet. Bitte wenden Sie sich bei Fragen an Ihre Vorgesetzten.
        </p>
      </div>
    }

    <!-- Why THIS form is on screen: a first fill needs no explanation, a re-confirmation does. The two flag
         sources are worded apart deliberately — "abgelaufen" is a stale answer the employee simply re-confirms,
         "geändert" is a form that changed under an answer they already gave. -->
    @if (!loading && !blockedNoAction && currentTask?.kind === 'expired') {
      <div class="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm" data-test="task-expired">
        <p class="text-sm font-bold text-yellow-800 tracking-tight">Gültigkeit abgelaufen</p>
        <p class="mt-1 text-sm text-gray-600 leading-relaxed">
          Die Gültigkeitsdauer dieses Formulars ist abgelaufen. Ihre bisherigen Angaben sind vorausgefüllt —
          bitte prüfen Sie sie, aktualisieren Sie was sich geändert hat, und senden Sie das Formular erneut ab.
        </p>
      </div>
    }
    @if (!loading && !blockedNoAction && currentTask?.kind === 'review') {
      <div class="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm" data-test="task-review">
        <p class="text-sm font-bold text-yellow-800 tracking-tight">Formular geändert</p>
        <p class="mt-1 text-sm text-gray-600 leading-relaxed">
          Dieses Formular wurde geändert, Ihre Angaben müssen deshalb überprüft werden. Sie sind vorausgefüllt —
          bitte prüfen Sie sie, ergänzen Sie was fehlt, und senden Sie das Formular erneut ab.
        </p>
      </div>
    }

    <!-- Active required form -->
    @if (!loading && !blockedNoAction && current) {
      <forms-dynamic-form [definition]="current" [initialValue]="currentInitialValue"
                          (submitForm)="onFormSubmit($event)"
                          (saveDraft)="onSaveDraft($event)"></forms-dynamic-form>
    }

  </div>
</div>
